import { useContext, useState } from 'react'
import ShortUniqueId from 'short-unique-id'
import _ from "lodash"

import SpeedDial from '@mui/material/SpeedDial'
import SpeedDialIcon from '@mui/material/SpeedDialIcon'
import SpeedDialAction from '@mui/material/SpeedDialAction'

import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import TurnLeftIcon from '@mui/icons-material/TurnLeft'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import CloseIcon from '@mui/icons-material/Close'

import { AppContext } from '/providers/app-context'
import { PassContext } from "../../../pass-context"
import dataSelector from '../../data-selector'
import PopupEditor from '../../popup-editor'
import fieldFormatter from "./field-formatter.js"

const uuid = new ShortUniqueId({ length: 16 })
const shortUUID = () => uuid.rnd()

// TODO : We can create textModules/linkModuele data in the class and use that for fixed templates...
// So we have CLASS - fixed for all passes of this class
// OBJECT - we can create groups of passes within this class with different fixed values.
// eg. We could have a class for NCP with fixed NCP info,
// then a template for each individual car park with info that differns between each car park
// instance then only needs to have car / date / time info
// INSTANCE - unique for each pass

// Is it worth implementing this?
// We'd need to be able to create multiple object definitions for each class.
// This is markedly different to what we have now... and is totally different to Apple.

const GoogleFields = () => {
  try {
    const { classData, objectData, setClassData, setGoogleBoth, instanceData, scannerInstanceData, preview } = useContext(PassContext)

    const insertRow = (rowIdx, direction) => {

      let cd = _.cloneDeep(classData)

      // Create Object item
      const newItemid = shortUUID()

      cd.textModulesData.push({
        id: newItemid,
        header: "NEW ITEM",
        body: ""
      })

      // Create new class item
      const newClassItem = {
        oneItem: {
          item: {
            firstValue: {
              fields: [{
                fieldPath: `class.textModulesData['${newItemid}']`
              }]
            }
          }
        }
      }

      // Insert new row into class data
      cd.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos.splice(Math.max(0, rowIdx + direction), 0, newClassItem)

      // Save class 
      setClassData(cd)
    }

    const moveRow = (rowIdx, direction) => {
      let cd = _.cloneDeep(classData)
      let od = _.cloneDeep(objectData)

      // Copy row we are moving
      let movingRow = _.cloneDeep(cd.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos[rowIdx])

      // Remove current row from class
      cd.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos.splice(rowIdx, 1)

      // Insert saved row at the appropriate position
      cd.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos.splice(rowIdx + direction, 0, movingRow)

      // Save class and object data
      setGoogleBoth({
        googleClass: cd,
        googleObject: od
      })
    }

    const deleteRow = rowIdx => {
      let cd = _.cloneDeep(classData)
      let od = _.cloneDeep(objectData)

      // Delete current row
      cd.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos.splice(rowIdx, 1)

      // Save class and object data
      setGoogleBoth({
        googleClass: cd,
        googleObject: od
      })
    }

    let fields = classData.classTemplateInfo.cardTemplateOverride.cardRowTemplateInfos

    if (fields.length) return (
      <>
        {fields?.map((row, idx) => <Row preview={preview} key={idx} rowCount={fields.length} rowIdx={idx} row={row} classData={classData} objectData={objectData} instanceData={instanceData} scannerInstanceData={scannerInstanceData} insertRow={insertRow} moveRow={moveRow} deleteRow={deleteRow} />)}
      </>
    )
    else return (
      <FieldEditor preview={preview} insertRow={insertRow} moveRow={moveRow} deleteRow={deleteRow} />
    )
  }
  catch (err) {
    console.error(err)
  }
}

export default GoogleFields

const rowItemCount = {
  oneItem: 1,
  twoItems: 2,
  threeItems: 3
}

const FieldEditor = ({ preview, insertRow, moveRow, deleteRow }) => (
  <div style={{ position: "relative", marginTop: 0, marginLeft: 1, marginRight: -1, marginBottom: -1 }} >
    <RowEditorSpeedDial preview={preview} rowCount={0} insertRow={insertRow} moveRow={moveRow} deleteRow={deleteRow} />
  </div>
)

const Row = ({ preview, rowCount, row, rowIdx, classData, objectData, instanceData, scannerInstanceData, insertRow, moveRow, deleteRow }) => {

  try {
    const rowType = Object.keys(row)[0]
    const itemCount = rowItemCount[rowType]

    var items = []

    switch (itemCount) {
      case 1: { items = [row[rowType].item]; break }
      case 2: { items = [row[rowType].startItem, row[rowType].endItem]; break }
      case 3: { items = [row[rowType].startItem, row[rowType].middleItem, row[rowType].endItem]; break }
    }

    return (
      <div style={{ position: "relative", marginTop: 0, marginLeft: 1, marginRight: -1, marginBottom: -1 }} >
        <PopupEditor title={"Content row " + (rowIdx + 1)} path="frontFields" row={rowIdx} type="fields">
          <div className="flex flex-row w-full justify-between editable" key={"ffrow-" + rowIdx} style={{ minHeight: 70 }}>
            {items?.map((item, idx) => <Item item={item} key={idx} idx={idx} count={itemCount} classData={classData} objectData={objectData} instanceData={instanceData} scannerInstanceData={scannerInstanceData} />)}
          </div>
          {rowIdx < (rowCount - 1) && <hr />}
        </PopupEditor>
        <RowEditorSpeedDial preview={preview} rowCount={rowCount} rowIdx={rowIdx} insertRow={insertRow} moveRow={moveRow} deleteRow={deleteRow} />
      </div>
    )
  }
  catch (err) {
    throw err
  }
}

const RowEditorSpeedDial = ({ rowCount, rowIdx, insertRow, moveRow, deleteRow, preview }) => {

  const { speeddialVisible, setSpeeddialVisible } = useContext(AppContext)
  const [open, setOpen] = useState(false)

  const handleSpeedDialClick = e => {
    e.preventDefault()
    const isOpen = !open
    setOpen(isOpen)
    setSpeeddialVisible(isOpen)
  }


  if (!preview) {
    const actions = rowCount ? rowCount < 3 ? [
      { icon: <TurnLeftIcon />, name: 'Insert row above', disabled: rowCount === 0, onClick: (e => insertRow(rowIdx, 0)) },
      { icon: <TurnLeftIcon sx={{ transform: "scale(1,-1)" }} />, name: 'Insert row below', disabled: false, onClick: (e => insertRow(rowIdx, 1)) },
      { icon: <KeyboardDoubleArrowUpIcon />, name: 'Move row up', disabled: (rowIdx < 1), onClick: (e => moveRow(rowIdx, -1)) },
      { icon: <KeyboardDoubleArrowDownIcon />, name: 'Move row down', disabled: (rowIdx === rowCount - 1), onClick: (e => moveRow(rowIdx, 1)) },
      { icon: <DeleteForeverIcon />, name: 'Delete row', disabled: rowCount === 0, onClick: (e => deleteRow(rowIdx)), colour: "red" },
    ]
      : [ // We have 3 rows. Can't have nay more,.
        { icon: <KeyboardDoubleArrowUpIcon />, name: 'Move row up', disabled: (rowIdx < 1), onClick: (e => moveRow(rowIdx, -1)) },
        { icon: <KeyboardDoubleArrowDownIcon />, name: 'Move row down', disabled: (rowIdx === rowCount - 1), onClick: (e => moveRow(rowIdx, 1)) },
        { icon: <DeleteForeverIcon />, name: 'Delete row', disabled: rowCount === 0, onClick: (e => deleteRow(rowIdx)), colour: "red" }
      ]
      : // We don't have any rows, so just show an insert option.
      [
        { icon: <TurnLeftIcon />, name: 'Insert row', disabled: false, onClick: (e => insertRow(0, 0)) },
      ]

    return (
      <SpeedDial
        ariaLabel="Row options"
        sx={{ position: 'absolute', top: 5, right: -105, textWrap: "nowrap" }}
        icon={<SpeedDialIcon icon={<ArrowBackIosNewIcon />} openIcon={<CloseIcon />} />}
        direction="down"
        onClick={handleSpeedDialClick}
        open={open}
        hidden={speeddialVisible && !open}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            disabled={action.disabled}
            onClick={action.onClick}
            sx={{ color: action.colour }}
            slotProps={{
              tooltip: {
                title: action.name,
                open: false,
                placement: 'right',
              }
            }}
          />
        ))}
      </SpeedDial>
    )
  }
}



const Item = ({ item, idx, count, classData, objectData, instanceData, scannerInstanceData }) => {

  try {
    let classes = "fields flex flex-col flex-1 overflow-hidden"

    switch (count) {
      case 2: {
        if (idx === 1) classes += " items-end"
        break
      }
      case 3: {
        if (idx === 1) classes += " items-center text-center"
        if (idx === 2) classes += " items-end text-right"
        break
      }
    }

    const key = item.firstValue.fields[0].fieldPath.replace(/[^'"]*['"]([^'"]*)['"][^'"]*/, "$1")
    const source = item.firstValue.fields[0].fieldPath.replace(/(object|class)\.textModulesData\[['"]([^'"]*)['"]\]/, "$1")
    const itemData = source === "class" ?
      classData.textModulesData?.filter(m => m.id === key)[0]
      :
      objectData.textModulesData?.filter(m => m.id === key)[0]

    const resolvedItemData = dataSelector({ objectData: { itemData }, instanceData, scannerInstanceData, path: "itemData" })

    return (
      <div className={classes} >
        <div className="label">{resolvedItemData?.header}</div>
        <div className="value">{fieldFormatter(resolvedItemData)}</div>
      </div>
    )
  }
  catch (err) {
    throw err
  }
}